import React, { useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserShield, faKey, faUserTag, faEnvelope, faClock, faSearch,
  faTrashAlt, faCheckCircle, faTimesCircle, faFilter, faUserEdit, faShieldAlt,
  faHardHat, faBriefcase, faGhost, faCheck, faPlus, faLock, faCopy, faPhone
} from '@fortawesome/free-solid-svg-icons';
import styles from '../Dashboard.module.css';
import pb from '../../lib/pocketbase';
import { toast } from 'react-toastify';

const Users = () => {
  const { users, refreshData } = useOutletContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [profileFilter, setProfileFilter] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Create new user states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newUser, setNewUser] = useState({ name: '', email: '', phone: '', role: '', profile_type: 'staff', password: '', passwordConfirm: '' });
  const [showCredentials, setShowCredentials] = useState(null);

  const [updating, setUpdating] = useState(false);

  const getProfileTypeInfo = (type) => {
    switch (type) {
      case 'staff': return { bg: '#e0f2fe', color: '#0ea5e9', label: 'Collaborateur' };
      case 'client': return { bg: '#dbeafe', color: '#2563eb', label: 'Client' };
      case 'candidate': return { bg: '#fef3c7', color: '#d97706', label: 'Candidat' };
      case 'visitor': return { bg: '#f1f5f9', color: '#64748b', label: 'Nouveau Visiteur' };
      default: return { bg: '#f8fafc', color: '#94a3b8', label: type || 'Inconnu' };
    }
  };

  const getRoleInfo = (role) => {
    switch (role) {
      case 'admin': return { bg: '#fee2e2', color: '#ef4444', label: 'Super-Admin', icon: faShieldAlt };
      case 'foreman': return { bg: '#fef3c7', color: '#d97706', label: 'Équipe Terrain', icon: faHardHat };
      case 'worker': return { bg: '#dcfce7', color: '#16a34a', label: 'Équipe Terrain', icon: faHardHat };
      case 'employee': return { bg: '#e0f2fe', color: '#0ea5e9', label: 'Gestion & Bureau', icon: faBriefcase };
      case 'client': return { bg: '#dbeafe', color: '#2563eb', label: 'Espace Client', icon: faUserShield };
      default: return { bg: '#f1f5f9', color: '#64748b', label: 'Accès Restreint', icon: faGhost };
    }
  };

  const getAvatarColor = (name) => {
    const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];
    if (!name) return colors[0];
    const charCode = name.charCodeAt(0);
    return colors[charCode % colors.length];
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
  };

  const handleSelectUser = (id) => {
    setSelectedUsers(prev =>
      prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  const handleDeleteBulk = async () => {
    if (!window.confirm(`Supprimer définitivement les ${selectedUsers.length} accès sélectionnés ?`)) return;

    try {
      await Promise.all(selectedUsers.map(id => pb.collection('users').delete(id)));
      toast.success("Comptes supprimés avec succès");
      setSelectedUsers([]);
      refreshData();
    } catch (error) {
      toast.error("Erreur lors de la suppression en masse");
    }
  };

  const handleQuickEdit = (user) => {
    setEditingUser({ ...user });
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      await pb.collection('users').update(editingUser.id, {
        name: editingUser.name,
        phone: editingUser.phone,
        role: editingUser.role,
        profile_type: editingUser.profile_type,
        emailVisibility: true
      });
      toast.success("Accès mis à jour !");
      setIsEditModalOpen(false);
      refreshData();
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setUpdating(false);
    }
  };

  const generatePassword = () => {
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let retVal = "";
    for (let i = 0, n = charset.length; i < 10; ++i) {
      retVal += charset.charAt(Math.floor(Math.random() * n));
    }
    setNewUser({ ...newUser, password: retVal, passwordConfirm: retVal });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (newUser.password !== newUser.passwordConfirm) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    try {
      setUpdating(true);
      const usernameBase = newUser.email ? newUser.email.split('@')[0].replace(/[^a-zA-Z0-9]/g, '').toLowerCase() : 'user';
      const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
      const username = `${usernameBase}${randomNum}`;

      const payload = {
        ...newUser,
        username,
        emailVisibility: true
      };

      // Auto‑assign role according to selected profile_type
      if (payload.profile_type === 'client') payload.role = 'client';
      else if (payload.profile_type === 'candidate' || payload.profile_type === 'visitor') payload.role = 'user';
      else if (payload.profile_type === 'staff') payload.role = 'worker';

      // Remove empty optional fields that might cause validation errors
      if (!payload.phone) delete payload.phone;
      if (!payload.role) delete payload.role;

      const created = await pb.collection('users').create(payload);

      setShowCredentials({ email: newUser.email, password: newUser.password });
      setNewUser({ name: '', email: '', phone: '', role: '', profile_type: 'staff', password: '', passwordConfirm: '' });
      setIsCreateModalOpen(false);
      refreshData();
      toast.success("Compte créé avec succès !");
    } catch (error) {
      console.error("PocketBase Create Error details:", JSON.stringify(error?.data || error, null, 2));
      let errorMsg = "Erreur lors de la création";
      if (error.data && error.data.data) {
        const fieldErrors = Object.entries(error.data.data)
          .map(([field, err]) => `${field}: ${err.message}`)
          .join(', ');
        errorMsg += ` - ${fieldErrors}`;
      } else if (error.message) {
        errorMsg += `: ${error.message}`;
      }
      toast.error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  const handleInlineUpdate = async (userId, field, value) => {
    try {
      let updateData = { [field]: value };
      
      // Auto-sync role when profile_type changes
      if (field === 'profile_type') {
        if (value === 'client') updateData.role = 'client';
        else if (value === 'candidate' || value === 'visitor') updateData.role = 'user';
        else if (value === 'staff') updateData.role = 'worker'; // Default back to worker
      }

      await pb.collection('users').update(userId, updateData);
      toast.success("Mise à jour réussie");
      refreshData();
    } catch (error) {
      toast.error("Erreur de mise à jour");
    }
  };

  const handleResetPassword = async (email) => {
    if (!window.confirm(`Envoyer un email de réinitialisation à ${email} ?`)) return;
    try {
      await pb.collection('users').requestPasswordReset(email);
      toast.success("Email envoyé !");
    } catch (error) {
      toast.error("Erreur d'envoi");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.info("Copié dans le presse-papier");
  };

  const filteredUsers = useMemo(() => {
    return (users || []).filter(user => {
      const matchesSearch =
        (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesProfile = profileFilter === 'all' || user.profile_type === profileFilter;
      return matchesSearch && matchesRole && matchesProfile;
    });
  }, [users, searchTerm, roleFilter, profileFilter]);

  return (
    <div>
      <div className={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <h2 style={{ margin: 0 }}>Accès & Sécurité</h2>
          <button
            className={styles.addButton}
            onClick={() => setIsCreateModalOpen(true)}
            style={{ height: '40px', padding: '0 20px', display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <FontAwesomeIcon icon={faPlus} /> Créer un accès
          </button>
        </div>

        <div className={styles.headerActions}>
          <div style={{ position: 'relative', display: 'flex', gap: '10px' }}>
            <div className={styles.searchBar} style={{ position: 'relative' }}>
              <FontAwesomeIcon icon={faSearch} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                type="text"
                placeholder="Chercher un accès..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ paddingLeft: '35px', borderRadius: '20px', border: '1px solid #e2e8f0', height: '40px', width: '220px', background: 'white' }}
              />
            </div>
            <select
              className={styles.filterSelect}
              value={profileFilter}
              onChange={(e) => setProfileFilter(e.target.value)}
              style={{ padding: '0 15px', borderRadius: '20px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b' }}
            >
              <option value="all">Toutes les catégories</option>
              <option value="staff">Équipe</option>
              <option value="client">Clients</option>
              <option value="candidate">Recrutement</option>
              <option value="visitor">Visiteurs</option>
            </select>
            <select
              className={styles.filterSelect}
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{ padding: '0 15px', borderRadius: '20px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b' }}
            >
              <option value="all">Tous les rôles</option>
              <option value="admin">Administrateurs</option>
              <option value="employee">Collaborateurs</option>
              <option value="client">Clients</option>
              <option value="user">Visiteurs/Candidats</option>
            </select>
          </div>
        </div>
      </div>

      {showCredentials && (
        <div className={styles.bulkActionsBar} style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ fontWeight: '800' }}>✅ Compte créé ! Identifiants à transmettre :</div>
            <div style={{ fontSize: '0.9rem' }}>
              Email: <strong>{showCredentials.email}</strong> | MDP: <strong>{showCredentials.password}</strong>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => copyToClipboard(`Email: ${showCredentials.email}\nMot de passe: ${showCredentials.password}`)} style={{ background: '#16a34a', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer' }}>
              <FontAwesomeIcon icon={faCopy} /> Copier
            </button>
            <button onClick={() => setShowCredentials(null)} style={{ background: 'transparent', color: '#16a34a', border: '1px solid #16a34a', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer' }}>Fermer</button>
          </div>
        </div>
      )}

      {selectedUsers.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--color-primary)', fontWeight: '700' }}>
            <FontAwesomeIcon icon={faUserEdit} />
            {selectedUsers.length} accès sélectionnés
          </div>
          <button className={styles.deleteBtn} onClick={handleDeleteBulk} style={{ background: '#ef4444', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '10px', cursor: 'pointer', fontWeight: '600' }}>
            <FontAwesomeIcon icon={faTrashAlt} /> Supprimer en masse
          </button>
        </div>
      )}

      <div className={styles.dataSection}>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedUsers.length > 0 && selectedUsers.length === filteredUsers.length}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>Identité / ID</th>
                <th>Contact</th>
                <th>Catégorie Profil</th>
                <th>Rôle Accès</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map(user => {
                  const roleInfo = getRoleInfo(user.role);
                  return (
                    <tr key={user.id} className={selectedUsers.includes(user.id) ? styles.selectedRow : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedUsers.includes(user.id)}
                          onChange={() => handleSelectUser(user.id)}
                        />
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div
                            className={styles.userAvatarInitials}
                            style={{ background: getAvatarColor(user.name) }}
                          >
                            {getInitials(user.name)}
                          </div>
                          <div>
                            <div style={{ fontWeight: '700', color: user.name ? 'inherit' : '#94a3b8' }}>{user.name || 'Nom non défini'}</div>
                            <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>ID: {user.id}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem', color: 'var(--color-primary)', fontWeight: '600' }}>
                            <FontAwesomeIcon icon={faEnvelope} style={{ opacity: 0.3 }} />
                            <span>{user.email}</span>
                          </div>
                          {user.phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.85rem', color: '#64748b' }}>
                              <FontAwesomeIcon icon={faPhone} style={{ opacity: 0.3 }} />
                              <span>{user.phone}</span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <select
                          value={user.profile_type}
                          onChange={(e) => handleInlineUpdate(user.id, 'profile_type', e.target.value)}
                          style={{
                            backgroundColor: getProfileTypeInfo(user.profile_type).bg,
                            color: getProfileTypeInfo(user.profile_type).color,
                            fontWeight: '700',
                            border: '1px solid transparent',
                            cursor: 'pointer',
                            outline: 'none',
                            appearance: 'none',
                            WebkitAppearance: 'none',
                            MozAppearance: 'none',
                            padding: '6px 28px 6px 14px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${encodeURIComponent(getProfileTypeInfo(user.profile_type).color)}'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 10px center',
                            backgroundSize: '12px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.filter = 'brightness(0.95)';
                            e.target.style.transform = 'translateY(-1px)';
                            e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.filter = 'none';
                            e.target.style.transform = 'none';
                            e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                          }}
                        >
                          <option value="staff">Collaborateur (Équipe)</option>
                          <option value="client">Client (Propriétaire)</option>
                          <option value="candidate">Candidat (Recrutement)</option>
                          <option value="visitor">Nouveau Visiteur</option>
                        </select>
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                          {(user.role ? true : false) && user.profile_type !== 'visitor' ? (
                            <select
                              value={user.role}
                              onChange={(e) => handleInlineUpdate(user.id, 'role', e.target.value)}
                              style={{
                                backgroundColor: roleInfo.bg,
                                color: roleInfo.color,
                                fontWeight: '700',
                                border: '1px solid transparent',
                                cursor: 'pointer',
                                outline: 'none',
                                appearance: 'none',
                                WebkitAppearance: 'none',
                                MozAppearance: 'none',
                                padding: '6px 28px 6px 14px',
                                borderRadius: '20px',
                                fontSize: '0.75rem',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='${encodeURIComponent(roleInfo.color)}'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: 'right 10px center',
                                backgroundSize: '12px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.filter = 'brightness(0.95)';
                                e.target.style.transform = 'translateY(-1px)';
                                e.target.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.filter = 'none';
                                e.target.style.transform = 'none';
                                e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                              }}
                            >
                              <option value="admin">Super-Administrateur</option>
                              <option value="employee">Gestion & Bureau</option>
                              <option value="worker">Équipe Terrain (Chantiers / Planning)</option>
                              <option value="client">Espace Client</option>
                              <option value="user">Accès Restreint</option>
                            </select>
                          ) : (
                            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>—</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          <div style={{ fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            {user.verified ? (
                              <><FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} /> Vérifié</>
                            ) : (
                              <><FontAwesomeIcon icon={faTimesCircle} style={{ color: '#94a3b8' }} /> En attente</>
                            )}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#94a3b8' }}>Créé le {new Date(user.created).toLocaleDateString()}</div>
                        </div>
                      </td>
                      <td className={styles.actions}>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button onClick={() => handleQuickEdit(user)} title="Édition rapide" style={{ color: 'var(--color-primary)' }}>
                            <FontAwesomeIcon icon={faUserEdit} />
                          </button>
                          <button onClick={() => handleResetPassword(user.email)} title="Reset Password" style={{ color: '#64748b' }}>
                            <FontAwesomeIcon icon={faKey} />
                          </button>
                          <button onClick={() => { if (window.confirm('Supprimer cet accès ?')) pb.collection('users').delete(user.id).then(() => { toast.success('Supprimé'); refreshData(); }) }} title="Supprimer" style={{ color: '#ef4444' }}>
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                    <FontAwesomeIcon icon={faGhost} size="3x" style={{ opacity: 0.1, marginBottom: '15px' }} />
                    <p>Aucun compte ne correspond à votre recherche.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* CREATE USER MODAL */}
      {isCreateModalOpen && (
        <div className={styles.modalOverlay} onClick={() => setIsCreateModalOpen(false)}>
          <div className={styles.messageModal} style={{ maxWidth: '500px' }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <FontAwesomeIcon icon={faPlus} />
                <h3>Créer un nouvel accès</h3>
              </div>
              <button className={styles.closeModalBtn} onClick={() => setIsCreateModalOpen(false)}>
                <FontAwesomeIcon icon={faGhost} />
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className={styles.modalBody}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div className={styles.formField}>
                    <label>Nom Complet</label>
                    <input type="text" required value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Jean Dupont" />
                  </div>
                  <div className={styles.formField}>
                    <label>Email (Sera l'identifiant)</label>
                    <input type="email" required value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="email@maisonquinard.fr" />
                  </div>
                  <div className={styles.formField}>
                    <label>Téléphone</label>
                    <input type="tel" value={newUser.phone} onChange={e => setNewUser({ ...newUser, phone: e.target.value })} placeholder="06 12 34 56 78" />
                  </div>
                  <div className={styles.formField}>
                    <label>Catégorie Profil</label>
                    <select value={newUser.profile_type} onChange={e => {
                      const val = e.target.value;
                      let newRole = newUser.role;
                      if (val === 'client') newRole = 'client';
                      else if (val === 'candidate' || val === 'visitor') newRole = 'user';
                      else if (val === 'staff' && (newRole === 'client' || newRole === 'user')) newRole = 'worker';
                      setNewUser({ ...newUser, profile_type: val, role: newRole });
                    }}>
                      <option value="staff">Collaborateur (Équipe)</option>
                      <option value="client">Client (Propriétaire)</option>
                      <option value="candidate">Candidat (Recrutement)</option>
                      <option value="visitor">Nouveau Visiteur</option>
                    </select>
                  </div>
                  <div className={styles.formField}>
                    <label>Rôle</label>
                    <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                      <option value="admin">Super-Administrateur</option>
                      <option value="employee">Gestion & Bureau</option>
                      <option value="worker">Équipe Terrain (Chantiers / Planning)</option>
                      <option value="client">Espace Client</option>
                      <option value="user">Accès Restreint</option>
                    </select>
                  </div>
                  <div className={styles.formField}>
                    <label>Mot de passe temporaire</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <input
                        type="text"
                        required
                        value={newUser.password}
                        onChange={e => setNewUser({ ...newUser, password: e.target.value, passwordConfirm: e.target.value })}
                        placeholder="••••••••"
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        onClick={generatePassword}
                        style={{
                          height: '46px',
                          padding: '0 20px',
                          borderRadius: '12px',
                          background: 'white',
                          border: '2px solid var(--color-primary-light)',
                          color: 'var(--color-primary)',
                          cursor: 'pointer',
                          fontWeight: '700',
                          fontSize: '0.85rem',
                          transition: 'all 0.2s',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => { e.target.style.background = 'var(--color-primary-light)'; }}
                        onMouseLeave={(e) => { e.target.style.background = 'white'; }}
                      >
                        Générer
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.modalFooter} style={{ gap: '10px' }}>
                <button type="button" className={styles.viewBtn} onClick={() => setIsCreateModalOpen(false)}>Annuler</button>
                <button type="submit" className={styles.addButton} disabled={updating} style={{ background: 'var(--color-primary)', color: 'white' }}>
                  {updating ? 'Création...' : 'Créer le compte'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QUICK EDIT MODAL */}
      {isEditModalOpen && editingUser && (
        <div className={styles.modalOverlay} onClick={() => setIsEditModalOpen(false)}>
          <div className={styles.messageModal} style={{ maxWidth: '450px' }} onClick={e => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitle}>
                <FontAwesomeIcon icon={faUserEdit} />
                <h3>Édition Rapide</h3>
              </div>
              <button className={styles.closeModalBtn} onClick={() => setIsEditModalOpen(false)}>
                <FontAwesomeIcon icon={faGhost} />
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className={styles.modalBody}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div className={styles.formField}>
                    <label>Nom Complet</label>
                    <input
                      type="text"
                      value={editingUser.name || ''}
                      onChange={e => setEditingUser({ ...editingUser, name: e.target.value })}
                      placeholder="Ex: Jean Dupont"
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Téléphone</label>
                    <input
                      type="tel"
                      value={editingUser.phone || ''}
                      onChange={e => setEditingUser({ ...editingUser, phone: e.target.value })}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div className={styles.formField}>
                    <label>Catégorie Profil</label>
                    <select
                      value={editingUser.profile_type || 'staff'}
                      onChange={e => {
                        const val = e.target.value;
                        let newRole = editingUser.role;
                        if (val === 'client') newRole = 'client';
                        else if (val === 'candidate' || val === 'visitor') newRole = 'user';
                        else if (val === 'staff' && (newRole === 'client' || newRole === 'user')) newRole = 'worker';
                        setEditingUser({ ...editingUser, profile_type: val, role: newRole });
                      }}
                    >
                      <option value="staff">Collaborateur (Équipe)</option>
                      <option value="client">Client (Propriétaire)</option>
                      <option value="candidate">Candidat (Recrutement)</option>
                      <option value="visitor">Nouveau Visiteur</option>
                    </select>
                  </div>
                  <div className={styles.formField}>
                    <label>Rôle</label>
                    <select
                      value={editingUser.role || 'user'}
                      onChange={e => setEditingUser({ ...editingUser, role: e.target.value })}
                    >
                      <option value="admin">Super-Administrateur</option>
                      <option value="employee">Gestion & Bureau</option>
                      <option value="worker">Équipe Terrain (Chantiers / Planning)</option>
                      <option value="client">Espace Client</option>
                      <option value="user">Accès Restreint</option>
                    </select>
                  </div>
                  <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', fontSize: '0.8rem', color: '#64748b' }}>
                    <strong>Email :</strong> {editingUser.email}<br />
                    <strong>ID :</strong> {editingUser.id}
                  </div>
                </div>
              </div>
              <div className={styles.modalFooter} style={{ gap: '10px' }}>
                <button type="button" className={styles.viewBtn} onClick={() => setIsEditModalOpen(false)}>Annuler</button>
                <button type="submit" className={styles.addButton} disabled={updating} style={{ background: 'var(--color-primary)', color: 'white' }}>
                  <FontAwesomeIcon icon={faCheck} /> {updating ? 'Mise à jour...' : 'Confirmer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
